import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Lesson, LessonCategory, Project } from '../../../types';
import {
  getProjectLessons, createLesson, updateLesson, deleteLesson,
} from '../../../services/lessonService';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TEXTAREA, BM_TITLE_INPUT,
} from '../../../components/BoardModal';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const CATEGORY_LABEL: Record<number, string> = {
  [LessonCategory.Technical]:     'Technical',
  [LessonCategory.Process]:       'Process',
  [LessonCategory.Communication]: 'Communication',
  [LessonCategory.Risk]:          'Risk',
  [LessonCategory.Resource]:      'Resource',
  [LessonCategory.Other]:         'Other',
};
const CATEGORY_BADGE: Record<number, { bg: string; fg: string }> = {
  [LessonCategory.Technical]:     { bg: '#D4EEF5', fg: '#0A5568' },
  [LessonCategory.Process]:       { bg: '#E0E7FF', fg: '#3730A3' },
  [LessonCategory.Communication]: { bg: '#FEF3C7', fg: '#92400E' },
  [LessonCategory.Risk]:          { bg: '#FEE2E2', fg: '#991B1B' },
  [LessonCategory.Resource]:      { bg: '#D1FAE5', fg: '#065F46' },
  [LessonCategory.Other]:         { bg: '#F3F4F6', fg: '#5A6272' },
};

type CatFilter = 'all' | 1 | 2 | 3 | 4 | 5 | 6;
const FILTER_OPTIONS: { value: CatFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: LessonCategory.Technical, label: 'Technical' },
  { value: LessonCategory.Process, label: 'Process' },
  { value: LessonCategory.Communication, label: 'Communication' },
  { value: LessonCategory.Risk, label: 'Risk' },
  { value: LessonCategory.Resource, label: 'Resource' },
  { value: LessonCategory.Other, label: 'Other' },
];

export default function LessonsTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_lessons">
      <LessonsTabBody {...props} />
    </ModuleGuard>
  );
}

function LessonsTabBody({ pca, projectId }: Props) {
  const me = useCurrentUser(pca, projectId);
  const [items, setItems] = useState<Lesson[]>([]);
  const [filter, setFilter] = useState<CatFilter>('all');
  const [tagSearch, setTagSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<Lesson | null>(null);

  const reload = () => getProjectLessons(pca, projectId).then(setItems);
  useEffect(() => { reload(); }, [pca, projectId]); // eslint-disable-line

  const filtered = useMemo(() => {
    let list = items;
    if (filter !== 'all') list = list.filter(l => l.dagrun_pm_category === filter);
    const q = tagSearch.trim().toLowerCase();
    if (q) list = list.filter(l => (l.dagrun_pm_tags ?? '').toLowerCase().includes(q));
    return list;
  }, [items, filter, tagSearch]);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{
        padding: '10px 14px', marginBottom: 14,
        background: 'var(--brand-mist)', borderLeft: '3px solid var(--brand)',
        borderRadius: 4, fontSize: 12, color: 'var(--ink-2)',
      }}>
        <strong style={{ color: 'var(--brand)' }}>AI Agent integration:</strong>{' '}
        AI Agent can surface relevant lessons at project start — configure in AI Agent settings.
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Lessons Learned</h3>
        <button type="button" onClick={() => setShowNew(true)} disabled={!me}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: me ? 1 : 0.5 }}
          onMouseEnter={e => me && (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Log Lesson</button>
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <FilterPills value={filter} options={FILTER_OPTIONS} onChange={setFilter} />
        <div style={{ flex: 1 }} />
        <input
          type="search"
          placeholder="Search tags…"
          value={tagSearch}
          onChange={e => setTagSearch(e.target.value)}
          style={{ ...BM_INPUT, width: 220, fontSize: 13, padding: '6px 10px' }}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>
          No lessons {filter === 'all' && !tagSearch ? 'logged yet' : 'match this filter'}.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Title</Th><Th>Category</Th><Th>Phase</Th><Th>Logged By</Th><Th>Logged On</Th><Th>Tags</Th>
          </tr></thead>
          <tbody>
            {filtered.map(l => (
              <tr key={l.dagrun_pm_lessonid} onClick={() => setSelected(l)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{l.dagrun_pm_name}</span></Td>
                <Td>{l.dagrun_pm_category != null
                  ? <Badge {...CATEGORY_BADGE[l.dagrun_pm_category]}>{CATEGORY_LABEL[l.dagrun_pm_category]}</Badge>
                  : <Em />}</Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{l.dagrun_pm_phase ?? <Em />}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{l.dagrun_pm_loggedbyname ?? <Em />}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                  {l.dagrun_pm_loggedon ? new Date(l.dagrun_pm_loggedon).toLocaleDateString() : '—'}
                </span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                  {l.dagrun_pm_tags ?? <Em />}
                </span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && me && (
        <CreateLessonModal pca={pca} projectId={projectId} meOid={me.oid} meName={me.displayName}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {selected && (
        <LessonDrawer pca={pca} lesson={selected}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
const Em = () => <span style={{ color: '#B8BEC9', fontSize: 13 }}>—</span>;

function CreateLessonModal({ pca, projectId, meOid, meName, onClose, onCreated }: {
  pca: IPublicClientApplication; projectId: string; meOid: string; meName: string;
  onClose: () => void; onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<number>(LessonCategory.Process);
  const [description, setDescription] = useState('');
  const [impact, setImpact] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [phase, setPhase] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createLesson(pca, {
        projectId,
        name: name.trim(),
        category,
        description: description.trim() || null,
        impact: impact.trim() || null,
        recommendation: recommendation.trim() || null,
        loggedbyid: meOid, loggedbyname: meName,
        phase: phase.trim() || null,
        tags: tags.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Log Lesson" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>
          {saving ? 'Logging…' : 'Log Lesson'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Title *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="Short, specific lesson title" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Category</label>
          <select value={category} onChange={e => setCategory(Number(e.target.value))} style={BM_INPUT}>
            {[LessonCategory.Technical, LessonCategory.Process, LessonCategory.Communication,
              LessonCategory.Risk, LessonCategory.Resource, LessonCategory.Other].map(v =>
              <option key={v} value={v}>{CATEGORY_LABEL[v]}</option>)}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Phase</label>
          <input value={phase} onChange={e => setPhase(e.target.value)} style={BM_INPUT} placeholder="e.g. Construction, UAT" />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} style={BM_TEXTAREA}
          placeholder="What happened? What was the situation?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Impact</label>
        <textarea value={impact} onChange={e => setImpact(e.target.value)} style={BM_TEXTAREA}
          placeholder="What was the effect on schedule, budget, quality, team, etc.?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Recommendation</label>
        <textarea value={recommendation} onChange={e => setRecommendation(e.target.value)} style={BM_TEXTAREA}
          placeholder="What should future projects do differently?" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Tags (comma-separated)</label>
        <input value={tags} onChange={e => setTags(e.target.value)} style={BM_INPUT}
          placeholder="e.g. vendor-risk, kickoff, qa" />
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function LessonDrawer({ pca, lesson, onClose, onDeleted }: {
  pca: IPublicClientApplication; lesson: Lesson; onClose: () => void; onDeleted: () => void;
}) {
  const [draft, setDraft] = useState<Lesson>(lesson);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(lesson); }, [lesson]);

  const patch = async (fields: Partial<Lesson>) => {
    setSaving(true);
    try {
      await updateLesson(pca, draft.dagrun_pm_lessonid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };

  const setAndPatch = <K extends keyof Lesson>(k: K, v: Lesson[K]) => {
    setDraft(d => ({ ...d, [k]: v })); patch({ [k]: v } as Partial<Lesson>);
  };
  const setText = <K extends keyof Lesson>(k: K, v: Lesson[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<Lesson>), 600);
  };
  const flush = <K extends keyof Lesson>(k: K, v: Lesson[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<Lesson>);
  };

  const doDelete = async () => {
    setSaving(true);
    try { await deleteLesson(pca, draft.dagrun_pm_lessonid); onDeleted(); } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 520,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>
            {savedFlash ? 'Saved ✓' : ''}
          </span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <input value={draft.dagrun_pm_name}
          onChange={e => setText('dagrun_pm_name', e.target.value)}
          onBlur={e => flush('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 12, width: '100%', background: 'transparent' }} />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={draft.dagrun_pm_category ?? LessonCategory.Process}
            onChange={e => setAndPatch('dagrun_pm_category', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[LessonCategory.Technical, LessonCategory.Process, LessonCategory.Communication,
              LessonCategory.Risk, LessonCategory.Resource, LessonCategory.Other].map(v =>
              <option key={v} value={v}>{CATEGORY_LABEL[v]}</option>)}
          </select>
          <span style={{ fontSize: 12, color: '#9198A8' }}>
            Logged by <strong style={{ color: '#5A6272' }}>{draft.dagrun_pm_loggedbyname ?? '—'}</strong>
            {draft.dagrun_pm_loggedon && (
              <span style={{ marginLeft: 8, fontFamily: 'var(--font-mono)' }}>
                {new Date(draft.dagrun_pm_loggedon).toLocaleDateString()}
              </span>
            )}
          </span>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Phase</label>
          <input value={draft.dagrun_pm_phase ?? ''}
            onChange={e => setText('dagrun_pm_phase', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_phase', e.target.value || null)} style={BM_INPUT} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Description</label>
          <textarea value={draft.dagrun_pm_description ?? ''}
            onChange={e => setText('dagrun_pm_description', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_description', e.target.value || null)}
            style={{ ...BM_TEXTAREA, minHeight: 100 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Impact</label>
          <textarea value={draft.dagrun_pm_impact ?? ''}
            onChange={e => setText('dagrun_pm_impact', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_impact', e.target.value || null)} style={BM_TEXTAREA} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Recommendation</label>
          <textarea value={draft.dagrun_pm_recommendation ?? ''}
            onChange={e => setText('dagrun_pm_recommendation', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_recommendation', e.target.value || null)} style={BM_TEXTAREA} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Tags</label>
          <input value={draft.dagrun_pm_tags ?? ''}
            onChange={e => setText('dagrun_pm_tags', e.target.value || null)}
            onBlur={e => flush('dagrun_pm_tags', e.target.value || null)} style={BM_INPUT} />
        </div>

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal open title="Delete Lesson" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_name}</strong>? This cannot be undone.
        </BoardModal>
      )}
    </>
  );
}
